import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useBusiness } from '@/contexts/BusinessContext';
import { useMonth } from '@/contexts/MonthContext';
import { supabase } from '@/lib/supabase';
import { Users, Plus, ChevronDown, RefreshCw, ListFilter as Filter, Search, X, Calendar } from 'lucide-react';
import LogoSpinner from '@/components/LogoSpinner';
import {
  getXmCustomers,
  getXmPayments,
  createXmCustomer,
  createXmPayment,
  deleteXmPayment,
  deleteXmCustomer,
  subscribeToXmCustomers,
  subscribeToXmPayments,
} from '@/lib/customersApi';
import { getGlobalCustomers, subscribeToGlobalCustomers } from '@/lib/databaseApi';
import type { XmCustomer, XmCustomerPayment, XmCardBrand, CardLimitGroup, GlobalCustomer } from '@/types/database';
import { CARD_LIMIT_GROUP, CARD_GROUP_LIMITS, CARD_GROUP_LABELS, XM_CARD_BRANDS } from '@/types/database';
import { getMonthName } from '@/lib/dateUtils';
import { getCardBrandColor } from './customers/cardBrandColors';
import AddPaymentModal from './customers/AddPaymentModal';
import CustomerDetailModal from './customers/CustomerDetailModal';

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

function formatPaymentDate(raw: string): string {
  const d = raw.includes('T') ? new Date(raw) : new Date(raw + 'T00:00:00');
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface PaymentEntry {
  amount: number;
  date: string;
  cardBrand: string;
}

interface LimitGroupSummary {
  group: CardLimitGroup;
  label: string;
  fullLimit: number;
  charged: number;
  available: number;
  brands: string[];
  entries: PaymentEntry[];
}

function computeGroupSummaries(payments: XmCustomerPayment[]): LimitGroupSummary[] {
  const groupMap = new Map<CardLimitGroup, { charged: number; brands: Set<string>; entries: PaymentEntry[] }>();
  for (const p of payments) {
    const group = CARD_LIMIT_GROUP[p.card_brand as XmCardBrand];
    if (!group) continue;
    const existing = groupMap.get(group) || { charged: 0, brands: new Set<string>(), entries: [] };
    existing.charged += Number(p.charged_amount);
    existing.brands.add(p.card_brand);
    existing.entries.push({
      amount: Number(p.charged_amount),
      date: p.payment_date,
      cardBrand: p.card_brand,
    });
    groupMap.set(group, existing);
  }

  return Array.from(groupMap.entries()).map(([group, data]) => {
    const fullLimit = CARD_GROUP_LIMITS[group];
    data.entries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    return {
      group,
      label: CARD_GROUP_LABELS[group],
      fullLimit,
      charged: data.charged,
      available: fullLimit - data.charged,
      brands: Array.from(data.brands),
      entries: data.entries,
    };
  });
}

export default function CustomersPage() {
  const { selectedBusiness } = useBusiness();
  const { selectedMonth, selectMonth } = useMonth();
  const [customers, setCustomers] = useState<XmCustomer[]>([]);
  const [payments, setPayments] = useState<XmCustomerPayment[]>([]);
  const [globalCustomers, setGlobalCustomers] = useState<GlobalCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [detailCustomerId, setDetailCustomerId] = useState<string | null>(null);
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const [cardFilter, setCardFilter] = useState<XmCardBrand | 'all'>('all');
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const year = selectedMonth.year;
  const month = selectedMonth.month;
  const monthYear = `${year}-${String(month).padStart(2, '0')}`;
  const monthYearRef = useRef(monthYear);
  monthYearRef.current = monthYear;

  const fetchAndSet = useCallback(async (businessId: string, my: string) => {
    const [cData, pData, gData] = await Promise.all([
      getXmCustomers(businessId, my),
      getXmPayments(businessId, my),
      getGlobalCustomers(),
    ]);
    if (monthYearRef.current !== my) return;
    setCustomers(cData);
    setPayments(pData);
    setGlobalCustomers(gData);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!selectedBusiness) return;
    setCustomers([]);
    setPayments([]);
    setLoading(true);
    setDetailCustomerId(null);
    setSelectedCustomerId(null);

    fetchAndSet(selectedBusiness.id, monthYear).catch(() => {
      if (monthYearRef.current === monthYear) setLoading(false);
    });
  }, [selectedBusiness, monthYear, fetchAndSet]);

  useEffect(() => {
    if (!selectedBusiness) return;
    const businessId = selectedBusiness.id;

    const reload = () => {
      const my = monthYearRef.current;
      fetchAndSet(businessId, my).catch(() => {});
    };
    const ch1 = subscribeToXmCustomers(businessId, reload);
    const ch2 = subscribeToXmPayments(businessId, reload);
    const ch3 = subscribeToGlobalCustomers(reload);

    return () => {
      supabase.removeChannel(ch1);
      supabase.removeChannel(ch2);
      supabase.removeChannel(ch3);
    };
  }, [selectedBusiness, fetchAndSet]);

  const paymentsByCustomer = useMemo(() => {
    const map = new Map<string, XmCustomerPayment[]>();
    for (const p of payments) {
      const list = map.get(p.customer_id) || [];
      list.push(p);
      map.set(p.customer_id, list);
    }
    return map;
  }, [payments]);

  const customerRows = useMemo(() => {
    return customers.map(c => {
      const cPayments = paymentsByCustomer.get(c.id) || [];
      const groups = computeGroupSummaries(cPayments);
      const totalCharged = groups.reduce((s, g) => s + g.charged, 0);
      const totalLimit = groups.reduce((s, g) => s + g.fullLimit, 0);
      const totalAvailable = totalLimit - totalCharged;
      return { customer: c, groups, totalCharged, totalLimit, totalAvailable, paymentCount: cPayments.length };
    });
  }, [customers, paymentsByCustomer]);

  const filteredRows = useMemo(() => {
    let rows = customerRows;
    if (cardFilter !== 'all') {
      rows = rows.filter(row => {
        const cPayments = paymentsByCustomer.get(row.customer.id) || [];
        return cPayments.some(p => p.card_brand === cardFilter);
      });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      rows = rows.filter(row => row.customer.name.toLowerCase().includes(q));
    }
    return rows;
  }, [customerRows, cardFilter, paymentsByCustomer, searchQuery]);

  const grandTotals = useMemo(() => {
    return filteredRows.reduce(
      (acc, r) => ({
        charged: acc.charged + r.totalCharged,
        limit: acc.limit + r.totalLimit,
        available: acc.available + r.totalAvailable,
      }),
      { charged: 0, limit: 0, available: 0 }
    );
  }, [filteredRows]);

  const filterStats = useMemo(() => {
    if (cardFilter === 'all') return null;
    let paid = 0;
    let total = 0;
    for (const row of customerRows) {
      const cPayments = paymentsByCustomer.get(row.customer.id) || [];
      const hasCard = cPayments.some(p => p.card_brand === cardFilter);
      total++;
      if (hasCard) paid++;
    }
    return { paid, total };
  }, [cardFilter, customerRows, paymentsByCustomer]);

  const [refreshing, setRefreshing] = useState(false);

  function refreshData() {
    if (!selectedBusiness) return;
    setRefreshing(true);
    const my = monthYearRef.current;
    Promise.all([
      getXmCustomers(selectedBusiness.id, my),
      getXmPayments(selectedBusiness.id, my),
      getGlobalCustomers(),
    ]).then(([cData, pData, gData]) => {
      if (monthYearRef.current !== my) return;
      setCustomers(cData);
      setPayments(pData);
      setGlobalCustomers(gData);
    }).catch(() => {}).finally(() => {
      setRefreshing(false);
    });
  }

  async function handleAddPaymentFromGlobal(
    globalCustomerName: string,
    cardBrand: XmCardBrand,
    charged: number,
    limit: number,
    paymentDate: string
  ) {
    if (!selectedBusiness) return;
    try {
      let customer = customers.find(c => c.name === globalCustomerName);
      if (!customer) {
        customer = await createXmCustomer(selectedBusiness.id, globalCustomerName, monthYear);
      }
      await createXmPayment(selectedBusiness.id, customer.id, monthYear, cardBrand, charged, limit, paymentDate);
      setShowAddPayment(false);
      refreshData();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  }

  async function handleDeletePayment(paymentId: string) {
    try {
      await deleteXmPayment(paymentId);
      refreshData();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  }

  async function handleDeleteCustomer(customerId: string) {
    try {
      await deleteXmCustomer(customerId);
      setDetailCustomerId(null);
      refreshData();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  }

  function openAddPayment(customerId?: string) {
    setSelectedCustomerId(customerId || null);
    setShowAddPayment(true);
  }

  const now = new Date();
  const currentYear = now.getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear + 1];

  const selectedCustomerName = selectedCustomerId
    ? customers.find(c => c.id === selectedCustomerId)?.name || ''
    : '';

  const selectedCustomerPayments = selectedCustomerId
    ? (paymentsByCustomer.get(selectedCustomerId) || [])
    : [];

  const detailCustomer = detailCustomerId ? customers.find(c => c.id === detailCustomerId) : null;
  const detailPayments = detailCustomerId ? (paymentsByCustomer.get(detailCustomerId) || []) : [];

  if (loading && customers.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <LogoSpinner size={48} />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="glass-header shrink-0 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-white">Customers</h1>
            <p className="text-xs text-white mt-0.5 hidden sm:block">
              {filteredRows.length} customer{filteredRows.length !== 1 ? 's' : ''} &middot; {getMonthName(month)} {year}
              {filterStats && (
                <span className="ml-2 text-teal-600 font-semibold">
                  {filterStats.paid}/{filterStats.total} paid
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <div className="relative flex items-center">
              {searchOpen ? (
                <div className="flex items-center gap-1.5 bg-white/10 border border-white/10 rounded-xl px-2.5 py-1.5 transition-all duration-200">
                  <Search size={13} className="text-slate-400 shrink-0" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search..."
                    className="w-24 sm:w-40 text-xs text-white bg-transparent outline-none placeholder:text-slate-400"
                    autoFocus
                  />
                  <button
                    onClick={() => { setSearchQuery(''); setSearchOpen(false); }}
                    className="text-slate-400 hover:text-white/60 transition-colors"
                  >
                    <X size={13} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => { setSearchOpen(true); setTimeout(() => searchInputRef.current?.focus(), 50); }}
                  className="flex items-center gap-1.5 p-2 sm:px-3 sm:py-2 glass-button rounded-xl text-white hover:text-teal-600 text-xs font-medium"
                  title="Search customers"
                >
                  <Search size={14} />
                  <span className="hidden sm:inline">Search</span>
                </button>
              )}
            </div>
            <div className="relative">
              <button
                onClick={() => setFilterOpen(!filterOpen)}
                className={`flex items-center gap-1.5 p-2 sm:px-3 sm:py-2 rounded-xl text-xs font-medium ${
                  cardFilter !== 'all'
                    ? 'glass-button-active text-teal-300'
                    : 'glass-button text-white'
                }`}
              >
                <Filter size={13} />
                <span className="hidden sm:inline">{cardFilter === 'all' ? 'Filter' : cardFilter}</span>
              </button>
              {filterOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setFilterOpen(false)} />
                  <div className="absolute right-0 mt-1 w-48 bg-white rounded-xl shadow-xl border border-white/10 z-50 p-2 max-h-64 overflow-auto">
                    <button
                      onClick={() => { setCardFilter('all'); setFilterOpen(false); }}
                      className={`w-full text-left px-3 py-1.5 rounded-lg text-xs transition-all duration-150 ${
                        cardFilter === 'all' ? 'bg-teal-500/10 text-teal-700 font-semibold' : 'text-white glass-hover'
                      }`}
                    >
                      All Cards
                    </button>
                    {XM_CARD_BRANDS.map(brand => {
                      const color = getCardBrandColor(brand);
                      return (
                        <button
                          key={brand}
                          onClick={() => { setCardFilter(brand); setFilterOpen(false); }}
                          className={`w-full text-left px-3 py-1.5 rounded-lg text-xs transition-all duration-150 flex items-center gap-2 ${
                            cardFilter === brand ? 'bg-teal-500/10 text-teal-700 font-semibold' : 'text-white glass-hover'
                          }`}
                        >
                          <div className={`w-2 h-2 rounded-full ${color.dot}`} />
                          {brand}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            <button
              onClick={() => setMonthDropdownOpen(true)}
              className="flex items-center gap-1.5 p-2 sm:px-3 sm:py-2 glass-button rounded-xl text-xs font-medium text-white"
              title={`${getMonthName(month)} ${year}`}
            >
              <Calendar size={14} className="sm:hidden text-teal-400" />
              <span className="hidden sm:inline">{getMonthName(month)} {year}</span>
              <ChevronDown size={13} className="hidden sm:inline" />
            </button>
            <button
              onClick={refreshData}
              disabled={refreshing}
              className="flex items-center gap-1.5 p-2 sm:px-3 sm:py-2 glass-button rounded-xl text-white hover:text-teal-600 disabled:opacity-50 text-xs font-medium"
              title="Refresh"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={() => openAddPayment()}
              className="flex items-center gap-1.5 p-2 sm:px-4 sm:py-2 glass-button-active text-white rounded-xl text-xs font-medium"
              title="Add Payment"
            >
              <Plus size={14} />
              <span className="hidden sm:inline">Add Payment</span>
            </button>
          </div>
        </div>
        <p className="text-xs text-white mt-2 sm:hidden">
          {filteredRows.length} customer{filteredRows.length !== 1 ? 's' : ''} &middot; {getMonthName(month)} {year}
          {filterStats && (
            <span className="ml-2 text-teal-600 font-semibold">
              {filterStats.paid}/{filterStats.total} paid
            </span>
          )}
        </p>
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-5">
        {filteredRows.length === 0 && !loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="bg-white/10 rounded-2xl p-5 mb-4">
              <Users size={32} className="text-white" />
            </div>
            <h3 className="text-sm font-semibold text-white mb-1">
              {cardFilter !== 'all' ? `No customers with ${cardFilter}` : `No customers for ${getMonthName(month)} ${year}`}
            </h3>
            <p className="text-xs text-white mb-5">
              {cardFilter !== 'all' ? 'Try a different filter or add payments' : 'Add a payment to get started'}
            </p>
            <button
              onClick={() => openAddPayment()}
              className="flex items-center gap-1.5 px-4 py-2 glass-button-active text-white rounded-xl text-xs font-medium"
            >
              <Plus size={14} />
              Add Payment
            </button>
          </div>
        ) : (
          <>
            <div className="hidden lg:block">
              <div className="glass-panel rounded-2xl overflow-hidden">
                <table className="min-w-full divide-y divide-white/20">
                  <thead className="bg-white/20">
                    <tr>
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-white uppercase tracking-wider">Name</th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-white uppercase tracking-wider">Limit Groups</th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-white uppercase tracking-wider">Total Charged</th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-white uppercase tracking-wider">Full Limits</th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-white uppercase tracking-wider">Available</th>
                      <th className="px-4 py-3 text-right text-[11px] font-medium text-white uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/20">
                    {filteredRows.map(row => (
                      <tr
                        key={row.customer.id}
                        onClick={() => setDetailCustomerId(row.customer.id)}
                        className="glass-hover cursor-pointer"
                      >
                        <td className="px-4 py-3">
                          <span className="text-sm font-semibold text-white">{row.customer.name}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="space-y-1">
                            {row.groups.length === 0 ? (
                              <span className="text-xs text-white italic">No payments</span>
                            ) : (
                              row.groups.map(g => (
                                <div key={g.group} className="flex items-center gap-1.5">
                                  <div className="flex -space-x-1">
                                    {g.brands.map(b => {
                                      const color = getCardBrandColor(b);
                                      return <div key={b} className={`w-2 h-2 rounded-full ring-1 ring-white ${color.dot}`} />;
                                    })}
                                  </div>
                                  <span className="text-xs text-white">{g.label}</span>
                                </div>
                              ))
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="space-y-1.5">
                            {row.groups.length === 0 ? (
                              <span className="text-xs text-white">-</span>
                            ) : (
                              row.groups.map(g => (
                                <div key={g.group} className="space-y-0.5">
                                  {g.entries.map((entry, i) => (
                                    <div key={i} className="flex items-center gap-1.5">
                                      <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${getCardBrandColor(entry.cardBrand).dot}`} />
                                      <span className="text-xs font-medium text-white">${entry.amount.toFixed(2)}</span>
                                      <span className="text-[10px] text-slate-400">
                                        {formatPaymentDate(entry.date)}
                                      </span>
                                    </div>
                                  ))}
                                  <div className="text-[10px] font-semibold text-slate-500 pt-0.5">
                                    Total: ${g.charged.toFixed(2)}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="space-y-1">
                            {row.groups.length === 0 ? (
                              <span className="text-xs text-white">-</span>
                            ) : (
                              row.groups.map(g => (
                                <p key={g.group} className="text-xs font-medium text-white">${g.fullLimit.toFixed(2)}</p>
                              ))
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="space-y-1">
                            {row.groups.length === 0 ? (
                              <span className="text-xs text-white">-</span>
                            ) : (
                              row.groups.map(g => (
                                <p key={g.group} className="text-xs font-medium" style={{ color: g.available >= 0 ? '#22c55e' : '#dc2626' }}>
                                  ${g.available.toFixed(2)}
                                </p>
                              ))
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={e => { e.stopPropagation(); openAddPayment(row.customer.id); }}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-teal-700 bg-teal-500/10 rounded-lg glass-hover-teal"
                          >
                            <Plus size={12} />
                            Payment
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-white/30">
                    <tr>
                      <td className="px-4 py-3 text-xs font-bold text-white">Totals</td>
                      <td className="px-4 py-3" />
                      <td className="px-4 py-3 text-xs font-bold text-white">${grandTotals.charged.toFixed(2)}</td>
                      <td className="px-4 py-3 text-xs font-bold text-white">${grandTotals.limit.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-bold" style={{ color: grandTotals.available >= 0 ? '#22c55e' : '#dc2626' }}>
                          ${grandTotals.available.toFixed(2)}
                        </span>
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-3">
              {filteredRows.map(row => (
                <div
                  key={row.customer.id}
                  onClick={() => setDetailCustomerId(row.customer.id)}
                  className="glass-panel rounded-2xl p-4 cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-white">{row.customer.name}</h3>
                    <button
                      onClick={e => { e.stopPropagation(); openAddPayment(row.customer.id); }}
                      className="p-1.5 text-teal-600 rounded-lg glass-hover-teal"
                    >
                      <Plus size={14} />
                    </button>
                  </div>

                  {row.groups.length === 0 ? (
                    <p className="text-xs text-white italic">No payments this month</p>
                  ) : (
                    <div className="space-y-2.5">
                      {row.groups.map(g => (
                        <div key={g.group}>
                          <div className="flex items-center gap-2 text-[11px] mb-1">
                            <div className="flex -space-x-1 shrink-0">
                              {g.brands.map(b => {
                                const color = getCardBrandColor(b);
                                return <div key={b} className={`w-2 h-2 rounded-full ring-1 ring-white ${color.dot}`} />;
                              })}
                            </div>
                            <span className="text-white truncate flex-1">{g.label}</span>
                            <span className="text-white font-medium">${g.charged.toFixed(0)}</span>
                            <span className="text-white">/</span>
                            <span className="text-white">${g.fullLimit.toFixed(0)}</span>
                            <span className="ml-auto font-semibold" style={{ color: g.available >= 0 ? '#22c55e' : '#dc2626' }}>
                              ${g.available.toFixed(0)}
                            </span>
                          </div>
                          <div className="ml-5 space-y-0.5">
                            {g.entries.map((entry, i) => (
                              <div key={i} className="flex items-center gap-1.5 text-[10px]">
                                <div className={`w-1 h-1 rounded-full shrink-0 ${getCardBrandColor(entry.cardBrand).dot}`} />
                                <span className="text-white/60 font-medium">${entry.amount.toFixed(2)}</span>
                                <span className="text-slate-400">
                                  {formatPaymentDate(entry.date)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {row.groups.length > 0 && (
                    <div className="mt-3 pt-2.5 border-t border-white/10/60 flex items-center justify-between text-[11px]">
                      <span className="text-white">Total</span>
                      <div className="flex items-center gap-3">
                        <span className="text-white font-semibold">${row.totalCharged.toFixed(2)}</span>
                        <span className="font-bold" style={{ color: row.totalAvailable >= 0 ? '#22c55e' : '#dc2626' }}>
                          ${row.totalAvailable.toFixed(2)} avail
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="lg:hidden mt-4 glass-panel rounded-2xl p-4">
              <div className="flex items-center justify-between text-xs">
                <span className="font-bold text-white">Month Totals</span>
                <div className="flex items-center gap-4">
                  <div>
                    <span className="text-white">Charged: </span>
                    <span className="font-bold text-white">${grandTotals.charged.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-white">Limits: </span>
                    <span className="font-bold text-white">${grandTotals.limit.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-white">Avail: </span>
                    <span className="font-bold" style={{ color: grandTotals.available >= 0 ? '#22c55e' : '#dc2626' }}>
                      ${grandTotals.available.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {showAddPayment && (
        <AddPaymentModal
          globalCustomers={globalCustomers}
          preselectedCustomerName={selectedCustomerName}
          existingPayments={selectedCustomerPayments}
          allPayments={payments}
          allCustomers={customers}
          onClose={() => { setShowAddPayment(false); setSelectedCustomerId(null); }}
          onSave={handleAddPaymentFromGlobal}
        />
      )}

      {detailCustomer && (
        <CustomerDetailModal
          customerId={detailCustomer.id}
          customerName={detailCustomer.name}
          payments={detailPayments}
          onClose={() => setDetailCustomerId(null)}
          onDeletePayment={handleDeletePayment}
          onDeleteCustomer={handleDeleteCustomer}
        />
      )}

      {monthDropdownOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="glass-modal rounded-2xl w-full max-w-sm max-h-[50vh] flex flex-col">
            <div className="flex justify-between items-center p-5 border-b border-white/10">
              <h2 className="text-base font-bold text-white">Select Month</h2>
              <button onClick={() => setMonthDropdownOpen(false)} className="p-1.5 rounded-lg glass-hover">
                <X size={18} className="text-white" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-2">
              {yearOptions.map(y => (
                <div key={y}>
                  <p className={`text-[11px] font-bold uppercase tracking-wider px-2 pt-3 pb-1 ${y === year ? 'text-teal-400' : 'text-white/50'}`}>{y}</p>
                  {MONTHS.map(m => {
                    const my = `${y}-${String(m).padStart(2, '0')}`;
                    const isActive = my === monthYear;
                    return (
                      <button
                        key={my}
                        onClick={() => { selectMonth({ year: y, month: m }); setMonthDropdownOpen(false); }}
                        className={`w-full text-left px-4 py-2.5 rounded-xl text-sm mb-0.5 transition-all duration-150 ${
                          isActive ? 'bg-teal-500/10 text-teal-400 font-semibold' : 'text-white glass-hover'
                        }`}
                      >
                        {getMonthName(m)}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
